/**
 * LOCATION: dashboard-business/src/pages/Settings.js
 * 
 * NEW: Language Support section in AI Knowledge tab
 * - Basic: English only
 * - Pro: EN, YO, IG, HA, FR, ES (6 languages)
 * - Premium: ALL world languages
 * 
 * All other functionality unchanged from previous version.
 */

import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../hooks/useAuth';
import API from '../services/api';
import { ChevronDown, ChevronUp } from 'lucide-react';

const loadFA = () => {
  if (!document.getElementById('fa-cdn-settings')) {
    const fa = document.createElement('link');
    fa.id = 'fa-cdn-settings'; fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
    document.head.appendChild(fa);
  }
};

// ── Language definitions by plan ─────────────────────────────
const LANGUAGE_TIERS = {
  free:     { label: 'Basic', langs: ['English'], note: 'English only. Upgrade to Pro for 6 languages or Premium for all world languages.' },
  basic:    { label: 'Basic', langs: ['English'], note: 'English only. Upgrade to Pro for 6 languages or Premium for all world languages.' },
  pro:      { label: 'Pro',   langs: ['English', 'Yoruba', 'Igbo', 'Hausa', 'French', 'Spanish'], note: 'AI detects and responds in any of these 6 languages. Upgrade to Premium for all world languages.' },
  PREMIUM: { label: 'PREMIUM', langs: ['ALL WORLD LANGUAGES'], note: 'AI automatically detects and responds in any language in the world — Arabic, Chinese, Japanese, Korean, Russian, Portuguese, Hindi, Swahili, and every other language.' },
};

const LANG_FLAG = {
  'English': 'fa-flag-usa',   'Yoruba': 'fa-earth-africa', 'Igbo': 'fa-earth-africa',
  'Hausa': 'fa-earth-africa', 'French': 'fa-flag',         'Spanish': 'fa-earth-americas',
  'ALL WORLD LANGUAGES': 'fa-earth-europe',
};

// ── 20 AI Instruction Presets ─────────────────────────────────
const AI_PRESETS = [
  { id: 0,  label: 'Default',                 icon: 'fa-robot',           desc: 'Standard helpful AI behaviour',          text: 'Be helpful, accurate, and concise. Always stay on topic and represent the business professionally.' },
  { id: 1,  label: 'Upsell Gently',           icon: 'fa-tag',             desc: 'Naturally mention upgrades/products',    text: 'When relevant, naturally mention premium products or services that could benefit the customer. Never be pushy — suggest once and move on.' },
  { id: 2,  label: 'Always Greet by Name',    icon: 'fa-hand-wave',       desc: 'Personalise every response',             text: 'If the customer mentions their name at any point, use it naturally in subsequent replies to personalise the conversation.' },
  { id: 3,  label: 'Empathy First',           icon: 'fa-heart',           desc: 'Lead with understanding before solutions', text: 'Before providing any solution, always acknowledge the customer\'s feelings first. Use empathetic language like "I completely understand how frustrating that must be."' },
  { id: 4,  label: 'Strict Topic Boundary',   icon: 'fa-ban',             desc: 'Only answer business-related questions', text: 'Only answer questions directly related to this business, its products, services, and policies. Politely decline any off-topic requests and redirect the conversation.' },
  { id: 5,  label: 'Appointment Booking Focus', icon: 'fa-calendar-check', desc: 'Guide customers to book appointments', text: 'For any enquiry that could result in a service, always guide the customer toward booking an appointment. Mention availability, walk them through the booking process, and confirm their interest.' },
  { id: 6,  label: 'Fast & Brief',            icon: 'fa-bolt',            desc: 'Short sharp answers only',              text: 'Keep all responses under 3 sentences. Be direct and to the point. Customers prefer quick answers — avoid long explanations unless specifically asked.' },
  { id: 7,  label: 'Always Collect Contact Info', icon: 'fa-address-card', desc: 'Ask for email/phone before closing',  text: 'Before ending any conversation, politely ask for the customer\'s name and contact details (email or phone) so the team can follow up if needed.' },
  { id: 8,  label: 'Offer Discounts on Hesitation', icon: 'fa-percent',  desc: 'Give a discount hint when customer hesitates', text: 'If a customer seems hesitant about pricing or says something like "let me think about it" or "that\'s expensive", mention that special offers or discounts may be available and encourage them to ask.' },
  { id: 9,  label: 'Handle Complaints Calmly', icon: 'fa-shield-halved', desc: 'De-escalate complaints professionally', text: 'When a customer is upset or complaining, always remain calm, apologise sincerely without admitting legal liability, and promise that the team will personally follow up within 24 hours.' },
  { id: 10, label: 'WhatsApp Redirect',        icon: 'fa-brands fa-whatsapp', desc: 'Push complex queries to WhatsApp',  text: 'For complex queries that require detailed back-and-forth, suggest continuing the conversation on WhatsApp for faster and more personalised support. Provide the WhatsApp number from the contact info.' },
  { id: 11, label: 'After-Hours Message',      icon: 'fa-moon',            desc: 'Inform customers of off-hours status', text: 'If the current time is outside business hours, politely inform the customer that the team is currently unavailable, state when they will be back, and offer to take a message or email.' },
  { id: 12, label: 'Premium Brand Tone',       icon: 'fa-crown',           desc: 'Speak like a luxury brand',            text: 'Use elevated, refined language that conveys exclusivity and quality. Avoid casual slang. Customers should feel they\'re interacting with a premium, world-class brand.' },
  { id: 13, label: 'Nigerian Market Friendly', icon: 'fa-location-dot',    desc: 'Culturally tuned for Nigerian customers', text: 'Use warm, culturally aware language suited to Nigerian customers. Reference Naira (₦) for pricing. Mention common local payment methods (bank transfer, Paystack, Opay) where relevant.' },
  { id: 14, label: 'Highlight Reviews & Trust', icon: 'fa-star',           desc: 'Build trust through social proof',     text: 'When relevant, mention that the business has satisfied customers, positive reviews, or a strong track record. Build trust naturally without inventing specific statistics.' },
  { id: 15, label: 'Request Feedback at End',  icon: 'fa-comment-dots',    desc: 'Ask for a rating before closing',      text: 'At the natural end of every conversation, ask the customer to rate their experience from 1 to 5 and share any feedback. Keep it light and optional.' },
  { id: 16, label: 'Educational Style',        icon: 'fa-graduation-cap',  desc: 'Teach and explain before selling',     text: 'Before recommending a product or service, educate the customer about the topic. Help them understand why they need it, then position the business\'s offering as the solution.' },
  { id: 17, label: 'Urgency & Scarcity',       icon: 'fa-fire',            desc: 'Create gentle FOMO on offers',         text: 'When discussing products, services or promotions, create a gentle sense of urgency by mentioning limited availability, expiring offers, or high demand — only when truthfully applicable.' },
  { id: 18, label: 'Multilingual Ready',       icon: 'fa-language',        desc: 'Detect and match customer language',   text: 'If the customer writes in a language other than English (e.g. Yoruba, Igbo, Hausa, Pidgin, French), respond in the same language as best as possible. Prioritise clear communication over perfection.' },
  { id: 19, label: 'Warranty & Returns Focus', icon: 'fa-rotate-left',     desc: 'Proactively mention return policies',  text: 'When a customer is asking about products, proactively mention the return policy, warranty, or satisfaction guarantee to reduce purchase hesitation and build confidence.' },
];

const PLAN_PRESET_LIMIT = { free: 3, basic: 3, pro: 11, PREMIUM: 20 };

const THEMES = [
  { name: 'Default',  primary: '#000000', secondary: '#FFFFFF' },
  { name: 'Ocean',    primary: '#316E50', secondary: '#FF6E3C' },
  { name: 'Midnight', primary: '#121D31', secondary: '#D9C7BA' },
  { name: 'Sand',     primary: '#784C2D', secondary: '#F7E6CA' },
  { name: 'Mint',     primary: '#3EB489', secondary: '#FFFFFF' },
  { name: 'Copper',   primary: '#CC5500', secondary: '#FED8B1' },
  { name: 'Royal',    primary: '#0047AB', secondary: '#FFFFFF' },
  { name: 'Orchid',   primary: '#301934', secondary: '#C8A2C8' },
  { name: 'Mocha',    primary: '#362D26', secondary: '#EDE8D0' },
  { name: 'Slate',    primary: '#000000', secondary: '#7B7B7D' },
];

const BUSINESS_TYPES = [
  'E-commerce / Online Store','Restaurant / Food & Beverage',
  'Healthcare / Medical','Education / School','Real Estate',
  'Technology / Software','Fashion / Clothing','Financial Services',
  'Travel & Tourism','Beauty & Wellness','Logistics / Delivery',
  'Legal Services','Non-profit / NGO','Personal Website / Portfolio','Other'
];

const BEAUTY_SAMPLE = {
  companyName: 'GlowHaven Beauty & Wellness',
  welcomeMessage: 'Welcome to GlowHaven! How can we help you glow today?',
  themePrimary: '#C97B84', themeSecondary: '#FFF5F5',
  businessType: 'Beauty & Wellness',
  businessDescription: 'GlowHaven Beauty & Wellness is a premium beauty studio based in Lagos, Nigeria, offering a full range of skincare, haircare, and wellness treatments.',
  productsServices: '- Facials (Classic, Deep Cleanse, Anti-Ageing, Brightening)\n- Massages (Swedish, Deep Tissue, Hot Stone)\n- Manicure & Pedicure\n- Hair Treatments\n- Lash Extensions & Brow Shaping\n- Bridal Beauty Packages\n- Home Service available',
  pricing: '- Classic Facial: ₦12,000\n- Deep Tissue Massage: ₦22,000\n- Gel Manicure: ₦6,500\n- Bridal Package: ₦150,000–₦250,000',
  contactEmail: 'hello@glowhaven.ng', contactPhone: '+234 803 456 7890',
  address: '14 Admiralty Way, Lekki Phase 1, Lagos', isOnline: false,
  website: 'https://glowhaven.ng', businessHours: 'Mon–Sat: 9AM–8PM | Sun: 11AM–5PM',
  faqs: 'Q: Do I need to book?\nA: Yes, 24–48 hours advance booking recommended.\n\nQ: Do you accept transfers?\nA: Yes — GTBank, Zenith, Paystack, Opay, and cash.',
  aiInstructions: AI_PRESETS[0].text, aiTone: 'friendly',
};

const EMPTY = {
  companyName: '', welcomeMessage: 'Hi! How can we help you today?',
  themePrimary: '#000000', themeSecondary: '#FFFFFF',
  businessType: '', businessDescription: '', productsServices: '', pricing: '',
  contactEmail: '', contactPhone: '', address: '', isOnline: false,
  website: '', businessHours: '', faqs: '', aiInstructions: AI_PRESETS[0].text, aiTone: 'professional',
  widgetPosition: 'bottom-right', logoUrl: '', whatsappNumber: '',
};

// ── Collapsible Section ────────────────────────────────────────
const Section = ({ title, faIcon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={ss.section}>
      <button type="button" onClick={() => setOpen(!open)} style={ss.sHead}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className={`fa-solid ${faIcon}`} style={{ color: '#667eea', fontSize: 15 }}></i>
          <h3 style={ss.sTitle}>{title}</h3>
        </div>
        {open ? <ChevronUp size={17} color="#9ca3af" /> : <ChevronDown size={17} color="#9ca3af" />}
      </button>
      {open && <div style={ss.sBody}>{children}</div>}
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────
const Settings = () => {
  const { user } = useAuth();
  const [saving, setSaving]    = useState(false);
  const [saved, setSaved]      = useState(false);
  const [error, setError]      = useState('');
  const [activeTab, setActiveTab] = useState('widget');
  const [settings, setSettings]  = useState(EMPTY);
  const [selectedPresets, setSelectedPresets] = useState([0]);

  const plan        = user?.plan || 'basic';
  const isPREMIUM   = plan === 'PREMIUM';
  const isPro       = plan === 'pro' || isPREMIUM;
  const presetLimit = PLAN_PRESET_LIMIT[plan] || 3;
  const langTier    = LANGUAGE_TIERS[plan] || LANGUAGE_TIERS.basic;

  useEffect(() => { loadFA(); loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await API.getSettings();
      if (res.success && res.settings && res.settings.companyName) {
        setSettings(prev => ({ ...prev, ...res.settings }));
        try {
          const parsed = JSON.parse(res.settings.aiInstructions || '{}');
          if (parsed.presets) setSelectedPresets(parsed.presets);
          else setSelectedPresets([0]);
        } catch { setSelectedPresets([0]); }
      } else {
        setSettings(BEAUTY_SAMPLE);
        setSelectedPresets([0]);
      }
    } catch (e) { console.error('Load settings failed:', e); }
  };

  const set = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setSaved(false); setError('');
  };

  const togglePreset = (id) => {
    if (id === 0) return;
    if (selectedPresets.includes(id)) {
      setSelectedPresets(prev => prev.filter(p => p !== id));
    } else {
      if (selectedPresets.length >= presetLimit) {
        setError(`Your plan allows up to ${presetLimit} presets. Upgrade to add more.`);
        return;
      }
      setSelectedPresets(prev => [...prev, id]);
    }
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const combinedInstructions = selectedPresets
        .map(id => AI_PRESETS[id]?.text || '').filter(Boolean).join('\n\n');
      const payload = {
        ...settings,
        aiInstructions: JSON.stringify({ presets: selectedPresets, combined: combinedInstructions }),
      };
      const res = await API.updateSettings(payload);
      if (res.success !== false) {
        setSaved(true);
        setTimeout(() => setSaved(false), 4000);
      } else {
        setError(res.message || 'Failed to save — please try again');
      }
    } catch { setError('Network error — please try again'); }
    finally { setSaving(false); }
  };

  const SaveBtn = ({ label }) => (
    <button onClick={handleSave} disabled={saving} style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }}>
      <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : saved ? 'fa-circle-check' : 'fa-floppy-disk'}`} style={{ fontSize: 14 }}></i>
      {saving ? 'Saving...' : saved ? 'Saved!' : (label || 'Save Changes')}
    </button>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar />
      <main style={s.main}>

        <div style={s.headerRow}>
          <div>
            <h1 style={s.title}><i className="fa-solid fa-gear" style={{ marginRight: 10, color: '#667eea' }}></i>Settings</h1>
            <p style={s.subtitle}>Customize your widget and train your AI assistant</p>
          </div>
          <SaveBtn label="Save Changes" />
        </div>

        {error && <div style={s.errBanner}><i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }}></i>{error}</div>}
        {saved && <div style={s.okBanner}><i className="fa-solid fa-circle-check" style={{ marginRight: 8 }}></i>Settings saved! Your AI reflects these changes immediately.</div>}

        {/* Tabs */}
        <div style={s.tabs}>
          {[
            { id: 'widget', label: 'Widget',       icon: 'fa-paint-brush' },
            { id: 'ai',     label: 'AI Knowledge', icon: 'fa-robot' },
            { id: 'theme',  label: 'Themes',       icon: 'fa-swatchbook' },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ ...s.tab, ...(activeTab === tab.id ? s.tabActive : {}) }}>
              <i className={`fa-solid ${tab.icon}`} style={{ marginRight: 7, fontSize: 13 }}></i>
              {tab.label}
              {activeTab === tab.id && <div style={s.tabLine} />}
            </button>
          ))}
        </div>

        {/* ── WIDGET TAB ── */}
        {activeTab === 'widget' && (
          <div style={s.card}>
            <div style={s.fg}>
              <label style={s.lbl}><i className="fa-solid fa-building" style={s.li}></i>Company / Business Name</label>
              <input value={settings.companyName} onChange={e => set('companyName', e.target.value)} placeholder="e.g., GlowHaven Beauty" style={s.inp} />
            </div>
            <div style={s.fg}>
              <label style={s.lbl}><i className="fa-solid fa-comment" style={s.li}></i>Welcome Message</label>
              <textarea value={settings.welcomeMessage} onChange={e => set('welcomeMessage', e.target.value)} rows={3} style={{ ...s.inp, resize: 'vertical' }} />
              <p style={s.hint}>First message customers see when they open the chat</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              {[
                { field: 'themePrimary',   label: 'Primary Color',   hint: 'Widget header, buttons, user messages' },
                { field: 'themeSecondary', label: 'Secondary Color', hint: 'Text on primary backgrounds' },
              ].map(col => (
                <div key={col.field}>
                  <label style={s.lbl}><i className="fa-solid fa-palette" style={s.li}></i>{col.label}</label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="color" value={settings[col.field]} onChange={e => set(col.field, e.target.value)}
                      style={{ width: 44, height: 44, border: '2px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                    <input type="text" value={settings[col.field]} onChange={e => set(col.field, e.target.value)} maxLength={7} style={{ ...s.inp, flex: 1 }} />
                  </div>
                  <p style={s.hint}>{col.hint}</p>
                </div>
              ))}
            </div>
            <div style={s.fg}>
              <label style={s.lbl}><i className="fa-solid fa-arrows-left-right" style={s.li}></i>Widget Position</label>
              <div style={{ display: 'flex', gap: 12 }}>
                {[
                  { value: 'bottom-right', label: 'Bottom Right' },
                  { value: 'bottom-left',  label: 'Bottom Left'  },
                ].map(opt => {
                  const isSelected = (settings.widgetPosition || 'bottom-right') === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => set('widgetPosition', opt.value)}
                      style={{
                        flex: 1, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                        border: `2px solid ${isSelected ? '#667eea' : '#e5e7eb'}`,
                        background: isSelected ? '#eff6ff' : 'white',
                        color: isSelected ? '#4338ca' : '#374151',
                        fontWeight: 600, fontSize: 13, textAlign: 'center',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p style={s.hint}>Where the chat bubble sits on your website. Takes effect immediately — no need to change your embed code.</p>
            </div>
            <div style={s.fg}>
              <label style={s.lbl}><i className="fa-solid fa-image" style={s.li}></i>Widget Logo</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, border: '1px solid #e5e7eb',
                  background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden', flexShrink: 0,
                }}>
                  {settings.logoUrl
                    ? <img
                        src={settings.logoUrl}
                        alt="Logo preview"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    : <i className="fa-solid fa-image" style={{ color: '#d1d5db', fontSize: 16 }}></i>}
                </div>
                <input
                  type="url"
                  value={settings.logoUrl || ''}
                  onChange={e => set('logoUrl', e.target.value)}
                  placeholder="https://yourdomain.com/logo.png"
                  style={{ ...s.inp, flex: 1 }}
                />
              </div>
              <p style={s.hint}>
                Paste a direct link to a square logo image (PNG or SVG, at least 64×64px, transparent background works best).
                Shown in your chat widget's header and above the "Powered by" line. Leave blank to use the default Janimony logo.
              </p>
            </div>
            <div style={s.fg}>
              <label style={s.lbl}><i className="fa-brands fa-whatsapp" style={{ ...s.li, color: '#25d366' }}></i>WhatsApp Number</label>
              <input
                type="tel"
                value={settings.whatsappNumber || ''}
                onChange={e => set('whatsappNumber', e.target.value)}
                placeholder="e.g. 2348012345678 (with country code, no + or spaces)"
                style={s.inp}
              />
              <p style={s.hint}>
                Adds a "Chat us on WhatsApp" link in your widget's footer that opens a direct message to this number.
                Use the full international format with country code (e.g. Nigeria: <strong>234...</strong>, no leading zero, no "+", no spaces).
                Leave blank to hide the WhatsApp link entirely.
              </p>
            </div>
            {/* Preview */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <p style={{ fontSize: 12, color: '#9ca3af', padding: '10px 14px', margin: 0, background: '#f9fafb' }}>
                <i className="fa-solid fa-eye" style={{ marginRight: 6 }}></i>Widget Preview
              </p>
              <div style={{ background: settings.themePrimary, padding: '14px 16px', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: settings.themeSecondary, fontWeight: 600, fontSize: 14 }}>{settings.companyName || 'Support Assistant'}</span>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: settings.themeSecondary, opacity: 0.7, display: 'inline-block', marginTop: 4 }} />
              </div>
              <div style={{ padding: '14px 16px', background: '#f7f9fc', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#333', alignSelf: 'flex-start', maxWidth: '80%' }}>
                  {settings.welcomeMessage || 'Hi! How can we help you today?'}
                </div>
                <div style={{ background: settings.themePrimary, color: settings.themeSecondary, padding: '10px 14px', borderRadius: 10, fontSize: 13, alignSelf: 'flex-end', maxWidth: '60%' }}>
                  Hello, I need help!
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── AI KNOWLEDGE TAB ── */}
        {activeTab === 'ai' && (
          <div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
              <i className="fa-solid fa-robot" style={{ color: '#667eea', fontSize: 18, marginTop: 2 }}></i>
              <p style={{ margin: 0, fontSize: 14, color: '#1e40af', lineHeight: 1.6 }}>
                Tell the AI everything about your business. The more detail, the better it answers customers.
              </p>
            </div>

            {/* ── LANGUAGE SECTION ── */}
            <Section title="AI Language Support" faIcon="fa-language">
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Your Plan:</span>
                  <span style={{ padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 800, background: isPREMIUM ? '#ede9fe' : isPro ? '#eff6ff' : '#f3f4f6', color: isPREMIUM ? '#7c3aed' : isPro ? '#1e40af' : '#6b7280' }}>
                    <i className={`fa-solid ${isPREMIUM ? 'fa-crown' : isPro ? 'fa-gem' : 'fa-seedling'}`} style={{ marginRight: 5 }}></i>
                    {langTier.label}
                  </span>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  {langTier.langs.map(lang => (
                    <span key={lang} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: isPREMIUM ? '#ede9fe' : isPro ? '#eff6ff' : '#f9fafb', border: `1px solid ${isPREMIUM ? '#c4b5fd' : isPro ? '#bfdbfe' : '#e5e7eb'}`, borderRadius: 20, fontSize: 13, fontWeight: 600, color: isPREMIUM ? '#7c3aed' : isPro ? '#1e40af' : '#374151' }}>
                      <i className={`fa-solid ${LANG_FLAG[lang] || 'fa-globe'}`} style={{ fontSize: 11 }}></i>
                      {lang}
                    </span>
                  ))}
                </div>

                <p style={{ fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>
                  <i className="fa-solid fa-circle-info" style={{ marginRight: 6, color: '#667eea' }}></i>
                  {langTier.note}
                </p>

                {!isPREMIUM && (
                  <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!isPro && (
                      <a href="/billing" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, color: '#1e40af', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                        <i className="fa-solid fa-gem"></i> Upgrade to Pro — 6 Languages
                      </a>
                    )}
                    <a href="/billing" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: 8, color: '#7c3aed', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                      <i className="fa-solid fa-crown"></i> Upgrade to Premium — All World Languages
                    </a>
                  </div>
                )}

                {isPREMIUM && (
                  <div style={{ marginTop: 10, padding: '10px 14px', background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 10, fontSize: 13, color: '#5b21b6' }}>
                    <i className="fa-solid fa-earth-europe" style={{ marginRight: 7 }}></i>
                    <strong>All world languages active.</strong> Your AI automatically detects and responds in any language your customers write in — including Arabic, Chinese, Japanese, Korean, Hindi, Russian, Portuguese, Swahili, and every other language.
                  </div>
                )}
              </div>
            </Section>

            <Section title="Business Identity" faIcon="fa-building">
              <div style={s.fg}>
                <label style={s.lbl}>Business Type</label>
                <select value={settings.businessType} onChange={e => set('businessType', e.target.value)} style={s.inp}>
                  <option value="">— Select your business type —</option>
                  {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={s.fg}>
                <label style={s.lbl}>About Your Business</label>
                <textarea value={settings.businessDescription} onChange={e => set('businessDescription', e.target.value)} rows={6} style={{ ...s.inp, resize: 'vertical' }} />
                <p style={s.hint}>Be as detailed as possible — this is what the AI uses to understand your business</p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151', fontWeight: 600 }}>
                <input type="checkbox" checked={settings.isOnline} onChange={e => set('isOnline', e.target.checked)} style={{ width: 16, height: 16, accentColor: '#667eea' }} />
                This is a fully online business (no physical location)
              </label>
            </Section>

            <Section title="Products & Services" faIcon="fa-bag-shopping">
              <div style={s.fg}>
                <label style={s.lbl}>Products / Services You Offer</label>
                <textarea value={settings.productsServices} onChange={e => set('productsServices', e.target.value)} rows={8} style={{ ...s.inp, resize: 'vertical' }} />
              </div>
              <div style={s.fg}>
                <label style={s.lbl}>Pricing Information</label>
                <textarea value={settings.pricing} onChange={e => set('pricing', e.target.value)} rows={7} style={{ ...s.inp, resize: 'vertical' }} />
              </div>
            </Section>

            <Section title="Contact & Location" faIcon="fa-location-dot">
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1e40af' }}>
                <i className="fa-solid fa-circle-info" style={{ marginRight: 7 }}></i>
                These details are shown to Basic plan customers when they ask for a human agent.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={s.fg}>
                  <label style={s.lbl}><i className="fa-solid fa-envelope" style={s.li}></i>Contact Email</label>
                  <input type="email" value={settings.contactEmail} onChange={e => set('contactEmail', e.target.value)} style={s.inp} />
                </div>
                <div style={s.fg}>
                  <label style={s.lbl}><i className="fa-brands fa-whatsapp" style={{ ...s.li, color: '#25d366' }}></i>Phone / WhatsApp</label>
                  <input type="text" value={settings.contactPhone} onChange={e => set('contactPhone', e.target.value)} style={s.inp} />
                </div>
              </div>
              {!settings.isOnline && (
                <div style={s.fg}>
                  <label style={s.lbl}><i className="fa-solid fa-map-pin" style={s.li}></i>Physical Address</label>
                  <textarea value={settings.address} onChange={e => set('address', e.target.value)} rows={2} style={{ ...s.inp, resize: 'vertical' }} />
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={s.fg}>
                  <label style={s.lbl}><i className="fa-solid fa-globe" style={s.li}></i>Website</label>
                  <input type="text" value={settings.website} onChange={e => set('website', e.target.value)} style={s.inp} />
                </div>
                <div style={s.fg}>
                  <label style={s.lbl}><i className="fa-solid fa-clock" style={s.li}></i>Business Hours</label>
                  <input type="text" value={settings.businessHours} onChange={e => set('businessHours', e.target.value)} style={s.inp} />
                </div>
              </div>
            </Section>

            <Section title="FAQs & Common Questions" faIcon="fa-circle-question">
              <div style={s.fg}>
                <label style={s.lbl}>Frequently Asked Questions</label>
                <textarea value={settings.faqs} onChange={e => set('faqs', e.target.value)} rows={10} style={{ ...s.inp, resize: 'vertical' }} />
                <p style={s.hint}>The AI will use these to answer customer questions precisely</p>
              </div>
            </Section>

            <Section title="AI Personality & Instructions" faIcon="fa-wand-magic-sparkles">
              {/* Tone */}
              <div style={s.fg}>
                <label style={s.lbl}>AI Response Tone</label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {[
                    { v: 'professional', label: 'Professional', icon: 'fa-briefcase',  desc: 'Formal, clear, business-like' },
                    { v: 'friendly',     label: 'Friendly',     icon: 'fa-face-smile', desc: 'Warm, casual, approachable' },
                    { v: 'formal',       label: 'Formal',       icon: 'fa-user-tie',   desc: 'Very formal, corporate' },
                  ].map(tone => (
                    <label key={tone.v} onClick={() => set('aiTone', tone.v)} style={{ display: 'flex', flexDirection: 'column', padding: '12px 16px', border: `2px solid ${settings.aiTone === tone.v ? '#667eea' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', minWidth: 130, background: settings.aiTone === tone.v ? '#eff6ff' : 'white', transition: 'all 0.2s' }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 7 }}>
                        <i className={`fa-solid ${tone.icon}`} style={{ color: settings.aiTone === tone.v ? '#667eea' : '#9ca3af' }}></i>{tone.label}
                      </span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{tone.desc}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* AI Instruction Presets */}
              <div style={s.fg}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <label style={{ ...s.lbl, margin: 0 }}>
                    <i className="fa-solid fa-sliders" style={s.li}></i>AI Behaviour Presets
                  </label>
                  <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: isPREMIUM ? '#ede9fe' : isPro ? '#eff6ff' : '#f3f4f6', color: isPREMIUM ? '#7c3aed' : isPro ? '#1e40af' : '#6b7280', fontWeight: 700 }}>
                    {selectedPresets.length}/{presetLimit} selected
                  </span>
                </div>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px' }}>
                  Select presets to guide how your AI behaves. The AI combines all selected instructions.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                  {AI_PRESETS.map((preset, idx) => {
                    const isSelected = selectedPresets.includes(preset.id);
                    const isLocked   = idx >= presetLimit && !isSelected;
                    const isDefault  = preset.id === 0;
                    return (
                      <button key={preset.id} type="button"
                        onClick={() => !isLocked && !isDefault && togglePreset(preset.id)}
                        disabled={isLocked}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', background: isSelected ? '#eff6ff' : isLocked ? '#fafafa' : 'white', border: `2px solid ${isSelected ? '#667eea' : isLocked ? '#f3f4f6' : '#e5e7eb'}`, borderRadius: 12, cursor: isLocked ? 'not-allowed' : isDefault ? 'default' : 'pointer', opacity: isLocked ? 0.5 : 1, textAlign: 'left', transition: 'all 0.2s', position: 'relative' }}>
                        <div style={{ width: 34, height: 34, borderRadius: 9, background: isSelected ? '#667eea' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className={`fa-solid ${preset.icon}`} style={{ color: isSelected ? '#fff' : '#9ca3af', fontSize: 14 }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? '#1e40af' : '#111827', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {preset.label}
                            {isDefault && <span style={{ fontSize: 10, background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>Always On</span>}
                            {isSelected && !isDefault && <i className="fa-solid fa-circle-check" style={{ color: '#667eea', fontSize: 12 }}></i>}
                          </div>
                          <div style={{ fontSize: 12, color: '#9ca3af' }}>{preset.desc}</div>
                        </div>
                        {isLocked && <div style={{ position: 'absolute', top: 8, right: 8 }}><i className="fa-solid fa-lock" style={{ color: '#d1d5db', fontSize: 11 }}></i></div>}
                      </button>
                    );
                  })}
                </div>
                {selectedPresets.length > 0 && (
                  <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px' }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: '#374151', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <i className="fa-solid fa-eye" style={{ color: '#667eea' }}></i> Combined Instructions Preview
                    </p>
                    <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 120, overflowY: 'auto' }}>
                      {selectedPresets.map(id => AI_PRESETS[id]?.text).filter(Boolean).join('\n\n')}
                    </div>
                  </div>
                )}
              </div>
            </Section>
          </div>
        )}

        {/* ── THEMES TAB ── */}
        {activeTab === 'theme' && (
          <div style={s.card}>
            {!isPREMIUM && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <i className="fa-solid fa-lock" style={{ color: '#f59e0b', fontSize: 16 }}></i>
                <span style={{ fontSize: 14, color: '#92400e' }}>
                  Premium themes require <strong>PREMIUM</strong>.
                  <a href="/billing" style={{ color: '#667eea', marginLeft: 8, fontWeight: 700, textDecoration: 'none' }}>
                    Upgrade <i className="fa-solid fa-arrow-right" style={{ fontSize: 11 }}></i>
                  </a>
                </span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14 }}>
              {THEMES.map(theme => {
                const isSelected = settings.themePrimary === theme.primary && settings.themeSecondary === theme.secondary;
                const isLocked   = !isPREMIUM && theme.name !== 'Default';
                return (
                  <button key={theme.name} type="button" disabled={isLocked}
                    onClick={() => { set('themePrimary', theme.primary); set('themeSecondary', theme.secondary); }}
                    style={{ background: 'white', border: `2px solid ${isSelected ? '#667eea' : '#e5e7eb'}`, borderRadius: 12, padding: 14, cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.5 : 1, textAlign: 'left', boxShadow: isSelected ? '0 0 0 3px rgba(102,126,234,0.2)' : 'none', position: 'relative', transition: 'all 0.2s' }}>
                    <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', marginBottom: 8 }}>
                      <div style={{ background: theme.primary, padding: '8px 10px' }}>
                        <span style={{ color: theme.secondary, fontSize: 10, fontWeight: 600 }}>Header</span>
                      </div>
                      <div style={{ background: theme.primary, color: theme.secondary, padding: '5px 10px', margin: 5, borderRadius: 6, fontSize: 11, display: 'inline-block' }}>Hi!</div>
                    </div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: '0 0 2px' }}>{theme.name}</p>
                    <p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>{theme.primary}</p>
                    {isLocked && <span style={{ position: 'absolute', top: 8, right: 8, background: '#f59e0b', color: '#fff', fontSize: 9, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                      <i className="fa-solid fa-lock" style={{ marginRight: 3 }}></i>Premium
                    </span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <SaveBtn label="Save All Changes" />
        </div>
      </main>
    </div>
  );
};

const s = {
  main:      { flex: 1, padding: 30, background: '#f9fafb', overflowY: 'auto' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title:     { fontSize: 28, color: '#111827', margin: '0 0 4px', fontWeight: 800, display: 'flex', alignItems: 'center' },
  subtitle:  { color: '#6b7280', fontSize: 14, margin: 0 },
  saveBtn:   { display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  errBanner: { background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '12px 16px', borderRadius: 10, marginBottom: 16, fontSize: 14 },
  okBanner:  { background: '#dcfce7', border: '1px solid #86efac', color: '#166534', padding: '12px 16px', borderRadius: 10, marginBottom: 16, fontSize: 14 },
  tabs:      { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e5e7eb' },
  tab:       { position: 'relative', padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#9ca3af', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', transition: 'color 0.2s' },
  tabActive: { color: '#667eea' },
  tabLine:   { position: 'absolute', bottom: -1, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,#667eea,#764ba2)', borderRadius: 2 },
  card:      { background: '#fff', borderRadius: 14, padding: 26, border: '1px solid #e5e7eb' },
  fg:        { marginBottom: 18 },
  lbl:       { display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 7 },
  li:        { marginRight: 7, color: '#9ca3af', fontSize: 12 },
  inp:       { width: '100%', padding: '11px 14px', border: '2px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  hint:      { margin: '5px 0 0', fontSize: 12, color: '#9ca3af' },
};
const ss = {
  section: { background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', marginBottom: 12, overflow: 'hidden' },
  sHead:   { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', background: 'transparent', border: 'none', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' },
  sTitle:  { fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 },
  sBody:   { padding: '20px 22px' },
};

export default Settings;